import type { Action } from 'svelte/action';
import { gsap } from 'gsap';

interface Options {
	height?: number;
	onClose?: () => void;
	onOpen?: () => void;
	delayedEnable: number;
	snap: boolean;
}

export const FollowScroll: Action<HTMLElement, Partial<Options> | undefined> = (
	node: HTMLElement,
	props
) => {
	let lastScroll = window.scrollY;
	let timeoutId = 0;
	let state: 'opened' | 'closed' = 'closed';

	const scrollOffset = { value: 0 };

	const options: Options = {
		snap: true,
		delayedEnable: 0,
		...props
	};

	function update() {
		node.style.transform = `translateY(-${scrollOffset.value}px)`;
	}

	function getHeight() {
		return options.height ?? node.offsetHeight;
	}

	function onScroll() {
		gsap.killTweensOf(scrollOffset);
		clearTimeout(timeoutId);

		const delta = window.scrollY - lastScroll;
		lastScroll = window.scrollY;
		const targetScroll = scrollOffset.value + delta;
		const height = getHeight();

		scrollOffset.value = Math.max(0, Math.min(targetScroll, height));
		update();

		if (targetScroll < height && state === 'closed') {
			state = 'opened';
			options.onOpen?.();
		} else if (targetScroll >= height && state === 'opened') {
			state = 'closed';
			options.onClose?.();
		}

		timeoutId = window.setTimeout(() => {
			snapScroll();
		}, 100);
	}

	function snapScroll() {
		if (!options.snap) return;
		const height = getHeight();
		const target = Math.round(scrollOffset.value / height) * height;
		gsap.to(scrollOffset, {
			value: target,
			duration: 0.8,
			onUpdate: update,
			onComplete: () => {
				state = target === 0 ? 'opened' : 'closed';
				if (state === 'opened') {
					options.onOpen?.();
				} else {
					options.onClose?.();
				}
			},
			ease: 'expo.inOut'
		});
	}

	let delayedTimeout: number;
	if (options.delayedEnable > 0) {
		delayedTimeout = window.setTimeout(() => {
			lastScroll = window.scrollY;
			window.addEventListener('scroll', onScroll);
		}, options.delayedEnable);
	} else {
		window.addEventListener('scroll', onScroll);
	}
	return {
		destroy: () => {
			state = 'closed';
			window.removeEventListener('scroll', onScroll);
			gsap.killTweensOf(scrollOffset);
			clearTimeout(timeoutId);
			clearTimeout(delayedTimeout);
		}
	};
};
